import React from 'react';
import { 
    Palette, Lightbulb, Activity, Flag, CheckCircle2, ScanFace, 
    AlertTriangle, Layers, PaintBucket, Aperture, Eye, Brain,
    LayoutTemplate, Contrast, Target, Users, MessageSquare, ShieldCheck,
    MousePointer2, Fingerprint
} from "lucide-react";

// --- AESTHETIC HELPERS ---
const GlassContainer = ({ children, className = "" }) => (
    <div className={`bg-slate-900/40 border border-slate-700/50 rounded-xl overflow-hidden ${className}`}>
        {children}
    </div>
);

export const BrandTab = ({ brandAnalysis, messageConsistency, demographicAudit, psychographics, recommendationsList }) => {
    
    // Safety Check
    if (!brandAnalysis) return null;

    // --- 1. AUTHENTICITY FORENSICS ---
    const calculateAuthenticityRisk = () => {
        if (!recommendationsList || recommendationsList.length === 0) return null;
        
        let totalImages = recommendationsList.length;
        let imagesWithMarkers = 0;

        recommendationsList.forEach(rec => {
            const markers = rec.visual_rhetoric_analysis?.authenticity_differentiation?.generic_markers_detected || 0;
            if (markers > 0) imagesWithMarkers++;
        });

        const riskScore = imagesWithMarkers / totalImages; // 0 to 1
        
        if (riskScore > 0.5) return { 
            level: "High Risk", 
            desc: "Visuals may appear staged or 'stock-photo-like'.", 
            color: "text-rose-400", 
            bg: "bg-rose-500",
            percent: Math.round(riskScore * 100)
        };
        if (riskScore > 0.2) return { 
            level: "Moderate Risk", 
            desc: "Some generic elements detected. Ensure local context is visible.", 
            color: "text-amber-400", 
            bg: "bg-amber-500",
            percent: Math.round(riskScore * 100)
        };
        return { 
            level: "Authentic", 
            desc: "Strong local grounding. Minimal generic markers detected.", 
            color: "text-emerald-400", 
            bg: "bg-emerald-500",
            percent: Math.round(riskScore * 100)
        };
    };

    const authenticity = calculateAuthenticityRisk();

    // --- 2. AESTHETIC QUALITY AGGREGATE ---
    const calculateAestheticScore = () => {
        if (!recommendationsList || recommendationsList.length === 0) return 0;
        const total = recommendationsList.reduce((acc, r) => acc + (r.aesthetic_quality_score || 0), 0);
        return total / recommendationsList.length;
    };
    const avgAesthetic = calculateAestheticScore();

    // --- 3. TONAL DISTRIBUTION (Visual Physics) ---
    const getTonalBalance = () => {
        const dist = brandAnalysis.palette_distribution || { neutral: 0, "black/dark": 0, "white/light": 0 };
        const total = (dist.neutral || 0) + (dist["black/dark"] || 0) + (dist["white/light"] || 0) || 1;
        
        return {
            neutral: Math.round(((dist.neutral || 0) / total) * 100),
            dark: Math.round(((dist["black/dark"] || 0) / total) * 100),
            light: Math.round(((dist["white/light"] || 0) / total) * 100)
        };
    };
    const tone = getTonalBalance();

    // --- 4. ATTENTION DRIVERS (New Data) ---
    const getTopAttentionDrivers = () => {
        const drivers = {};
        if (recommendationsList && recommendationsList.length > 0) {
            recommendationsList.forEach(rec => {
                const list = rec.visual_rhetoric_analysis?.attention_capture_analysis?.attention_drivers || [];
                list.forEach(d => {
                    // Clean string (remove parens details)
                    const key = d.split('(')[0].trim();
                    drivers[key] = (drivers[key] || 0) + 1;
                });
            });
        }
        return Object.entries(drivers).sort((a, b) => b[1] - a[1]).slice(0, 3);
    };
    const attentionDrivers = getTopAttentionDrivers();

    // Helper to translate vibe into plain English
    const getVibeTranslation = (vibe) => {
        if (!vibe) return "No specific vibe detected.";
        if (vibe.includes("Calm")) return "Voters perceive this as STABLE and SAFE. Good for incumbents or 'safe pair of hands' messaging.";
        if (vibe.includes("Energy") || vibe.includes("Urgent")) return "Voters perceive this as URGENT and ACTIVE. Good for 'change' candidates.";
        if (vibe.includes("Warm") || vibe.includes("Positive")) return "Voters perceive this as WELCOMING. Good for community-building.";
        return "Voters perceive this as NEUTRAL. It may lack emotional stickiness.";
    };

    return (
        <div className="space-y-5 animate-in fade-in slide-in-from-bottom-4 duration-500 pb-8">
             
             {/* 1. PERCEIVED BRAND IDENTITY */}
             <section>
                <div className="flex items-center gap-2 mb-3 px-1">
                    <Palette size={18} className="text-purple-400" />
                    <h3 className="text-xs font-bold text-slate-300 uppercase tracking-widest">Perceived Brand Identity</h3>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 items-stretch">
                    
                    {/* Brand Consistency Score */}
                    <div className="bg-gradient-to-br from-purple-900/30 to-slate-900/50 p-5 rounded-xl border border-purple-500/20 flex flex-col justify-between">
                        <div>
                            <div className="text-[10px] text-purple-300 uppercase tracking-widest mb-1 font-bold">Visual Discipline</div>
                            <div className={`text-4xl font-black font-mono mb-2 ${
                                brandAnalysis.consistency_score > 0.7 ? 'text-emerald-400' : 
                                brandAnalysis.consistency_score > 0.5 ? 'text-amber-400' : 'text-rose-400'
                            }`}>
                                {Math.round(brandAnalysis.consistency_score * 100)}%
                            </div>
                        </div>
                        <p className="text-[10px] text-slate-400 leading-tight font-medium">
                            Measures how recognizable your materials are as "You" across all mediums.
                        </p>
                    </div>

                    {/* Psychological Signaling */}
                    <div className="bg-slate-900/40 p-5 rounded-xl border border-slate-700/50 col-span-2 flex flex-col justify-between">
                        <div className="flex flex-col md:flex-row items-start gap-6">
                            <div className="flex-1">
                                <div className="text-[10px] text-slate-400 uppercase tracking-widest mb-1 font-bold">Subconscious Signal</div>
                                <div className="text-xl font-bold text-white mb-2">
                                    "{brandAnalysis.psychological_vibe}"
                                </div>
                                <p className="text-xs text-slate-300 leading-relaxed font-medium italic border-l-2 border-purple-500 pl-3">
                                    {getVibeTranslation(brandAnalysis.psychological_vibe)}
                                </p>
                            </div>

                            {/* Political Flags */}
                            {brandAnalysis.political_color_flags && brandAnalysis.political_color_flags.length > 0 && (brandAnalysis.political_color_flags[0] !== "Neutral / Ambiguous") ? (
                                <div className="bg-slate-950/50 p-3 rounded-lg border border-slate-700/50 w-full md:w-1/3 mt-4 md:mt-0">
                                    <div className="flex items-center gap-1 text-[9px] text-orange-400 uppercase font-bold mb-2">
                                        <Flag size={10} /> Coded Signals
                                    </div>
                                    <ul className="space-y-1">
                                        {brandAnalysis.political_color_flags.map((flag, i) => (
                                            <li key={i} className="text-[10px] text-slate-300 flex items-center gap-2 font-medium">
                                                <div className="w-1.5 h-1.5 rounded-full bg-orange-500"></div>
                                                {flag}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ) : (
                                <div className="bg-emerald-500/10 p-3 rounded-lg border border-emerald-500/20 w-full md:w-1/3 mt-4 md:mt-0">
                                     <div className="flex items-center gap-1 text-[9px] text-emerald-400 uppercase font-bold mb-1">
                                        <CheckCircle2 size={10} /> Clean Signal
                                    </div>
                                    <p className="text-[10px] text-emerald-200/80 font-medium">No conflicting political color codes detected.</p>
                                </div>
                            )}
                        </div>
                        
                        {/* Traits */}
                        <div className="mt-4 flex flex-wrap gap-2">
                                {(brandAnalysis.brand_personality_profile || []).map((trait, i) => (
                                    <span key={i} className="px-2.5 py-1 bg-slate-800/50 text-slate-300 text-[10px] font-bold uppercase tracking-wide rounded-md border border-slate-700 flex items-center gap-1.5">
                                    <Activity size={10} className="text-purple-400"/> {trait}
                                    </span>
                                ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* 2. VISUAL PHYSICS & AESTHETICS */}
            <section>
                <GlassContainer className="p-5">
                    <div className="flex items-center gap-3 mb-5">
                        <div className="p-2 bg-pink-500/10 rounded-lg border border-pink-500/20">
                            <Aperture size={20} className="text-pink-400" />
                        </div>
                        <div>
                            <h3 className="text-lg font-bold text-slate-300">Visual Physics</h3>
                            <p className="text-xs text-slate-400 font-medium">Aesthetic quality, tonal balance, and visual weight</p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        
                        {/* Aesthetic Quality Gauge */}
                        <div className="bg-slate-950/30 p-4 rounded-xl border border-slate-800/50 flex flex-col justify-between">
                            <div>
                                <div className="flex items-center gap-2 mb-2">
                                    <LayoutTemplate size={14} className="text-pink-400" />
                                    <span className="text-[10px] font-bold text-pink-400 uppercase tracking-widest">Visual Polish</span>
                                </div>
                                <div className="text-2xl font-black text-white mb-1">
                                    {(avgAesthetic * 100).toFixed(0)}<span className="text-sm text-slate-500 font-normal">/100</span>
                                </div>
                                <div className="w-full bg-slate-800 rounded-full h-1.5 mb-2 overflow-hidden">
                                    <div className="h-full bg-pink-500 rounded-full" style={{ width: `${avgAesthetic * 100}%` }}></div>
                                </div>
                                <p className="text-[10px] text-slate-400 leading-tight font-medium">
                                    {avgAesthetic > 0.7 ? "High-end, professional production value." : avgAesthetic > 0.5 ? "Standard campaign quality." : "Raw, grassroots aesthetic."}
                                </p>
                            </div>
                        </div>

                        {/* Tonal Balance (Dark/Light) */}
                        <div className="bg-slate-950/30 p-4 rounded-xl border border-slate-800/50 flex flex-col justify-between col-span-1 md:col-span-2">
                            <div>
                                <div className="flex items-center gap-2 mb-3">
                                    <Contrast size={14} className="text-slate-400" />
                                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Tonal Distribution</span>
                                </div>
                                
                                <div className="flex items-center gap-1 h-8 w-full rounded-lg overflow-hidden border border-slate-700/50 mb-3">
                                    {tone.dark > 0 && (
                                        <div className="h-full bg-slate-800 flex items-center justify-center text-[9px] text-slate-400 font-bold" style={{ width: `${tone.dark}%` }}>
                                            {tone.dark > 15 && `Dark ${tone.dark}%`}
                                        </div>
                                    )}
                                    {tone.neutral > 0 && (
                                        <div className="h-full bg-slate-600 flex items-center justify-center text-[9px] text-slate-200 font-bold" style={{ width: `${tone.neutral}%` }}>
                                            {tone.neutral > 15 && `Neutral ${tone.neutral}%`}
                                        </div>
                                    )}
                                    {tone.light > 0 && (
                                        <div className="h-full bg-slate-300 flex items-center justify-center text-[9px] text-slate-900 font-bold" style={{ width: `${tone.light}%` }}>
                                            {tone.light > 15 && `Light ${tone.light}%`}
                                        </div>
                                    )}
                                </div>
                                
                                <div className="flex justify-between items-start">
                                    <p className="text-[10px] text-slate-400 font-medium leading-tight max-w-[80%]">
                                        {tone.dark > 50 ? "Heavily grounded. Conveys seriousness or threat." : 
                                         tone.light > 50 ? "Bright & airy. Conveys optimism and transparency." : 
                                         "Balanced neutral tones. Conveys stability and pragmatism."}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Authenticity Score (Moved Here) */}
                        {authenticity && (
                            <div className="bg-slate-950/30 p-4 rounded-xl border border-slate-800/50 flex flex-col justify-between">
                                <div>
                                    <div className="flex items-center gap-2 mb-2">
                                        <ScanFace size={14} className={authenticity.color} />
                                        <span className={`text-[10px] font-bold ${authenticity.color} uppercase tracking-widest`}>Authenticity</span>
                                    </div>
                                    <div className={`text-lg font-bold text-white mb-1 ${authenticity.color}`}>
                                        {authenticity.level}
                                    </div>
                                    <p className="text-[10px] text-slate-400 leading-tight font-medium">
                                        {authenticity.percent}% generic markers found. {authenticity.desc}
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                </GlassContainer>
            </section>

            {/* 3. [NEW] COGNITIVE-AESTHETIC FIT */}
            {psychographics && (
                <section>
                    <GlassContainer className="p-5">
                        <div className="flex items-center gap-3 mb-5">
                            <div className="p-2 bg-blue-500/10 rounded-lg border border-blue-500/20">
                                <Brain size={20} className="text-blue-400" />
                            </div>
                            <div>
                                <h3 className="text-lg font-bold text-slate-300">Cognitive-Aesthetic Fit</h3>
                                <p className="text-xs text-slate-400 font-medium">Does the visual style match the voter's brain?</p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Fit 1: Complexity vs Closure */}
                            <div className="bg-slate-950/30 p-4 rounded-xl border border-slate-800/50 flex items-start gap-4">
                                <div className="p-2 bg-slate-900 rounded-lg border border-slate-700 shrink-0">
                                    <Eye size={18} className="text-indigo-400" />
                                </div>
                                <div>
                                    <h4 className="text-xs font-bold text-white uppercase tracking-wider mb-1">Complexity Match</h4>
                                    <div className="text-[10px] text-slate-400 mb-2 font-medium">
                                        Audience Closure Need: <span className="text-white">{psychographics.need_cognitive_closure < 0.5 ? "LOW (Likes Detail)" : "HIGH (Needs Simple)"}</span>
                                    </div>
                                    <p className="text-xs text-slate-300 leading-relaxed">
                                        {psychographics.need_cognitive_closure < 0.5 && avgAesthetic > 0.6 
                                            ? "Strong Match. High-fidelity visuals appeal to this audience's desire for nuance and detail."
                                            : psychographics.need_cognitive_closure > 0.6 && avgAesthetic > 0.6
                                            ? "Risk. Visuals may be too complex. Ensure simple, bold focal points."
                                            : "Neutral Fit. Visual density is acceptable for this audience."}
                                    </p>
                                </div>
                            </div>

                            {/* Fit 2: Tone vs Threat */}
                            <div className="bg-slate-950/30 p-4 rounded-xl border border-slate-800/50 flex items-start gap-4">
                                <div className="p-2 bg-slate-900 rounded-lg border border-slate-700 shrink-0">
                                    <PaintBucket size={18} className="text-teal-400" />
                                </div>
                                <div>
                                    <h4 className="text-xs font-bold text-white uppercase tracking-wider mb-1">Tonal Resonance</h4>
                                    <div className="text-[10px] text-slate-400 mb-2 font-medium">
                                        Audience Threat Sensitivity: <span className="text-white">{psychographics.threat_sensitivity < 0.5 ? "LOW (Optimistic)" : "HIGH (Defensive)"}</span>
                                    </div>
                                    <p className="text-xs text-slate-300 leading-relaxed">
                                        {tone.dark > 40 && psychographics.threat_sensitivity < 0.5
                                            ? "Mismatch. Dark palette clashes with audience's optimistic, opportunity-driven mindset. Lighten the visuals."
                                            : tone.light > 40 && psychographics.threat_sensitivity > 0.5
                                            ? "Mismatch. Too bright. Audience wants security and seriousness. Darken the tone."
                                            : "Good Resonance. Palette weight aligns with voter emotional baseline."}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </GlassContainer>
                </section>
            )}

            {/* 4. [NEW SECTION] VISUAL DNA & ATTENTION ECONOMICS */}
            <section>
                <GlassContainer className="p-5">
                    <div className="flex items-center gap-3 mb-5">
                        <div className="p-2 bg-indigo-500/10 rounded-lg border border-indigo-500/20">
                            <Fingerprint size={20} className="text-indigo-400" />
                        </div>
                        <div>
                            <h3 className="text-lg font-bold text-slate-300">Visual DNA & Attention</h3>
                            <p className="text-xs text-slate-400 font-medium">Color identity and attention capture mechanisms</p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* 4A. Palette Swatches */}
                        <div className="bg-slate-950/30 p-4 rounded-xl border border-slate-800/50">
                            <div className="flex items-center gap-2 mb-3">
                                <Palette size={14} className="text-slate-400" />
                                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Campaign Palette</span>
                            </div>
                            <div className="flex items-center gap-2 mb-3">
                                {(brandAnalysis.campaign_color_palette || []).slice(0, 6).map((hex, i) => (
                                    <div key={i} className="flex flex-col items-center gap-1 group cursor-pointer">
                                        <div 
                                            className="w-8 h-8 rounded-full border border-slate-700 shadow-sm transition-transform hover:scale-110" 
                                            style={{ backgroundColor: hex }}
                                            title={hex}
                                        ></div>
                                        <span className="text-[9px] font-mono text-slate-500 opacity-0 group-hover:opacity-100 transition-opacity">{hex}</span>
                                    </div>
                                ))}
                            </div>
                            <p className="text-[10px] text-slate-400 leading-tight font-medium">
                                Detected dominant colors across all analyzed campaign assets.
                            </p>
                        </div>

                        {/* 4B. Top Attention Drivers */}
                        <div className="bg-slate-950/30 p-4 rounded-xl border border-slate-800/50">
                            <div className="flex items-center gap-2 mb-3">
                                <MousePointer2 size={14} className="text-indigo-400" />
                                <span className="text-[10px] font-bold text-indigo-400 uppercase tracking-widest">Top Attention Hooks</span>
                            </div>
                            <div className="space-y-2">
                                {attentionDrivers.length > 0 ? attentionDrivers.map(([driver, count], i) => (
                                    <div key={i} className="flex justify-between items-center text-xs border-b border-slate-800/50 pb-1.5 last:border-0 last:pb-0">
                                        <span className="text-slate-300 font-medium">{driver}</span>
                                        <span className="text-[9px] bg-slate-800 px-1.5 py-0.5 rounded text-slate-400 font-bold">x{count}</span>
                                    </div>
                                )) : (
                                    <div className="text-xs text-slate-500 italic">No specific drivers detected.</div>
                                )}
                            </div>
                        </div>
                    </div>
                </GlassContainer>
            </section>

             {/* 5. STRATEGIC AUDIT MATRIX (Replaces Scrolling Bar) */}
             <section>
                <GlassContainer className="p-5">
                    <div className="flex items-center gap-3 mb-5">
                        <div className="p-2 bg-emerald-500/10 rounded-lg border border-emerald-500/20">
                            <ShieldCheck size={20} className="text-emerald-400" />
                        </div>
                        <div>
                            <h3 className="text-lg font-bold text-slate-300">Strategic Audit Matrix</h3>
                            <p className="text-xs text-slate-400 font-medium">Cross-functional health check</p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        
                        {/* Audit 1: Brand Integrity */}
                        <div className="bg-slate-950/30 p-4 rounded-xl border border-slate-800/50 flex flex-col justify-between">
                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-2">
                                        <Target size={14} className="text-purple-400" />
                                        <span className="text-[10px] font-bold text-purple-400 uppercase tracking-widest">Brand</span>
                                    </div>
                                    <span className={`w-2 h-2 rounded-full ${brandAnalysis.consistency_score > 0.7 ? 'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]' : 'bg-amber-500'}`}></span>
                                </div>
                                <div className="text-sm font-bold text-white mb-1">
                                    {Math.round(brandAnalysis.consistency_score * 100)}% Consistency
                                </div>
                                <p className="text-[10px] text-slate-400 leading-tight font-medium">
                                    {brandAnalysis.consistency_score > 0.7 ? "High visual discipline." : "Inconsistent visual identity."}
                                </p>
                            </div>
                        </div>

                        {/* Audit 2: Message Discipline */}
                        {messageConsistency && (
                            <div className="bg-slate-950/30 p-4 rounded-xl border border-slate-800/50 flex flex-col justify-between">
                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="flex items-center gap-2">
                                            <MessageSquare size={14} className="text-cyan-400" />
                                            <span className="text-[10px] font-bold text-cyan-400 uppercase tracking-widest">Message</span>
                                        </div>
                                        <span className={`w-2 h-2 rounded-full ${messageConsistency.overall_consistency_score > 0.7 ? 'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]' : 'bg-amber-500'}`}></span>
                                    </div>
                                    <div className="text-sm font-bold text-white mb-1">
                                        {Math.round(messageConsistency.overall_consistency_score * 100)}% Coherence
                                    </div>
                                    <p className="text-[10px] text-slate-400 leading-tight font-medium">
                                        {messageConsistency.overall_consistency_score > 0.7 ? "Strong thematic repetition." : "Messaging is scattered."}
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* Audit 3: Demographic Fit */}
                        {demographicAudit && (
                            <div className="bg-slate-950/30 p-4 rounded-xl border border-slate-800/50 flex flex-col justify-between">
                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="flex items-center gap-2">
                                            <Users size={14} className="text-teal-400" />
                                            <span className="text-[10px] font-bold text-teal-400 uppercase tracking-widest">People</span>
                                        </div>
                                        <span className={`w-2 h-2 rounded-full ${(demographicAudit.age_representation?.gaps?.length || 0) === 0 ? 'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]' : 'bg-rose-500'}`}></span>
                                    </div>
                                    <div className="text-sm font-bold text-white mb-1">
                                        {(demographicAudit.age_representation?.gaps?.length || 0) === 0 ? "Representative" : "Gaps Detected"}
                                    </div>
                                    <p className="text-[10px] text-slate-400 leading-tight font-medium">
                                        {(demographicAudit.age_representation?.gaps?.length || 0) === 0 ? "Visuals match audience demographics." : "Key groups missing from visuals."}
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* Audit 4: Strategic Directive */}
                        <div className="bg-slate-950/30 p-4 rounded-xl border border-slate-800/50 flex flex-col justify-between">
                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-2">
                                        <Lightbulb size={14} className="text-amber-400" />
                                        <span className="text-[10px] font-bold text-amber-400 uppercase tracking-widest">Directive</span>
                                    </div>
                                    <span className="w-2 h-2 rounded-full bg-amber-500 shadow-[0_0_8px_rgba(245,158,11,0.5)]"></span>
                                </div>
                                <p className="text-[10px] text-slate-300 leading-tight font-medium italic line-clamp-3">
                                    "{brandAnalysis.recommendation || "Maintain current trajectory."}"
                                </p>
                            </div>
                        </div>

                    </div>
                </GlassContainer>
            </section>
        </div>
    );
};